<template>
  <div
    class="group relative overflow-hidden rounded-lg border-2 border-black-dark bg-white p-4 shadow-blue-hard-sm transition-all hover:shadow-blue-hard dark:bg-kekkle-dark-light"
  >
    <!-- Edit Button -->

    <pencil-alt-icon
      v-if="canEdit"
      class="absolute top-3 right-3 flex h-6 w-6 items-center justify-center dark:text-slate-200"
      @click="openEditModal"
    />

    <!-- Header -->
    <div class="mb-3">
      <h2 class="pr-10 text-lg font-bold text-gray-900 dark:text-slate-100">
        {{ item.name || "Untitled Event" }}
      </h2>
      <div
        class="mt-1 flex items-center gap-3 text-xs text-gray-600 dark:text-slate-400"
      >
        <span class="flex items-center gap-1">
          <span>📅</span>
          <span>{{ from }}</span>
        </span>
        <span class="flex items-center gap-1">
          <span>→</span>
          <span>{{ till }}</span>
        </span>
      </div>
      <p
        v-if="createdBy"
        class="mt-1 text-xs text-gray-500 dark:text-slate-500"
      >
        <span>✨</span> {{ createdBy }}
      </p>
    </div>

    <!-- Description -->
    <description-box v-if="item.description" class="mb-3">
      <p class="text-gray-700 dark:text-slate-300">
        {{ item.description }}
      </p>
    </description-box>

    <!-- Presence Section -->
    <div class="space-y-2">
      <!-- Going -->
      <PresenceButton
        :label="$t('components.agenda_items.present')"
        active-icon="✓"
        inactive-icon="👍"
        :is-active="isAvailable"
        :uuids="item.available"
        :disabled="updating"
        variant="green"
        @click="setAvailable"
      />

      <!-- Maybe -->
      <PresenceButton
        :label="$t('components.agenda_items.maybe')"
        active-icon="?"
        inactive-icon="🤔"
        :is-active="isMaybeAvailable"
        :uuids="item.maybe_available"
        :disabled="updating"
        variant="orange"
        @click="setMaybe"
      />

      <!-- Not Going -->
      <PresenceButton
        :label="$t('components.agenda_items.absent')"
        active-icon="✕"
        inactive-icon="👎"
        :is-active="isNotAvailable"
        :uuids="item.non_available"
        :disabled="updating"
        variant="red"
        @click="setNotAvailable"
      />
    </div>

    <!-- More Info Button -->
    <default-button
      color="blue"
      full-width
      class="mt-3"
      @click="goToEventDetails"
    >
      {{ $t("components.agenda_items.more_information") }} →
    </default-button>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from "vue";
import { useRouter } from "vue-router";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useEventManagement } from "@/composables/useEventManagement";
import { useEventPresence } from "@/composables/useEventPresence";
import { dateAgo } from "@/helpers/dates/date-ago";
import type { EventDocument } from "@kekkle/shared/types/documents/event-document/event-document";
import { PencilAltIcon } from "@heroicons/vue/outline";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import DescriptionBox from "@/components/ui/DescriptionBox.vue";
import PresenceButton from "./ui/PresenceButton.vue";

type Props = {
  item: EventDocument;
};

const props = defineProps<Props>();
const router = useRouter();

const groupsStore = useGroupsStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const { openEditEventBottomSheet } = useEventManagement();

// Use event presence composable
const eventRef = toRef(props, "item");
const {
  updating,
  isAvailable,
  isMaybeAvailable,
  isNotAvailable,
  setAvailable,
  setMaybe,
  setNotAvailable,
} = useEventPresence(eventRef);

const from = computed(() => dateAgo(props.item.begin_timestamp));
const till = computed(() => dateAgo(props.item.end_timestamp));

const members = computed(() => {
  return groupsStore.groupData?.members ?? {};
});

const createdBy = computed(() => {
  if (!props.item.created_by) return "";
  return members.value[props.item.created_by]?.name ?? "";
});

const canEdit = computed(() => {
  return groupsStore.isAdmin || props.item.created_by === authStore.authUuid;
});

function openEditModal() {
  openEditEventBottomSheet(props.item);
}

function goToEventDetails() {
  const groupId = userStore.activeGroup;
  if (!groupId) return;

  router.push({
    name: "viewEvent",
    params: {
      eventId: props.item._id,
      groupId,
    },
  });
}
</script>
